import classNames from 'classnames';
import { isString } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { ContainerOverflow } from '../ContainerOverflow/ContainerOverflow';

import './TableData.css';

export type TableColumnDataType = Array<JSX.Element | string> | React.ReactNode;

export enum TableSizeType {
    small = 'small',
    normal = 'normal'
}

export enum TableColumnAlignType {
    left = 'left',
    center = 'center',
    right = 'right'
}

export enum TableDataTheme {
    light = 'light',
    dark = 'dark',
    striped = 'striped'
}

export interface TableColumnData<T> {
    width: number | string;
    header: string | JSX.Element;
    cells: Array<T>;
    align?: TableColumnAlignType;
    cls?: string;
}

export interface TableDataProps {
    columns: TableColumnData<TableColumnDataType>[];
    theme?: TableDataTheme;
    size?: TableSizeType;
    rowHeight?: number;
    overflow?: boolean;
    highlightedRows?: number[];
    headerCls?: string;
}

export class TableData extends React.Component<TableDataProps> {
    render() {
        const {
            theme = TableDataTheme.light,
            size = TableSizeType.normal,
            headerCls
        } = this.props;

        return (
            <div
                className={Bem.cls({
                    block: 'table-data',
                    mods: { theme: theme, size: size }
                })}
            >
                <div
                    className={classNames(
                        Bem.cls('table-data', 'header'),
                        headerCls
                    )}
                >
                    {this.getTableDataHeader()}
                </div>
                <div className={Bem.cls('table-data', 'body')}>
                    {this.getTableDataBody()}
                </div>
            </div>
        );
    }

    private getTableDataHeader = () => {
        return this.props.columns.map(
            (
                {
                    header,
                    width,
                    align = TableColumnAlignType.left,
                    cls
                }: TableColumnData<TableColumnDataType>,
                key
            ) => (
                <div
                    style={{
                        width,
                        textAlign: align
                    }}
                    key={key}
                    className={classNames(
                        Bem.cls('table-data', 'header-cell', {
                            'custom-content': !isString(header)
                        }),
                        cls
                    )}
                    title={isString(header) ? header : undefined}
                >
                    {header}
                </div>
            )
        );
    };

    // Высота таблицы === кол-во ячеек в первой колонке
    private getTableDataBody = () => {
        const { columns, rowHeight, overflow, highlightedRows } = this.props;

        const body = columns[0].cells.reduce(
            (
                acc: Array<JSX.Element | Array<JSX.Element>>,
                value: TableColumnDataType,
                index: number
            ) => {
                const isHighlightedRow =
                    highlightedRows &&
                    highlightedRows.length > 0 &&
                    highlightedRows.includes(index);

                acc.push(
                    <div
                        key={index}
                        className={classNames(
                            Bem.cls('table-data', 'body-row'),
                            isHighlightedRow
                                ? Bem.cls('table-data', 'highlighted-row')
                                : ''
                        )}
                        style={{
                            height: rowHeight
                        }}
                    >
                        {this.getCellData(index)}
                    </div>
                );

                return acc;
            },
            []
        );

        if (overflow) {
            return <ContainerOverflow>{body}</ContainerOverflow>;
        }

        return body;
    };

    private getCellData = (index: number) => {
        const { overflow } = this.props;

        return this.props.columns.map(
            (
                {
                    cells,
                    width,
                    align,
                    cls
                }: TableColumnData<TableColumnDataType>,
                key
            ) => (
                <div
                    style={{
                        width,
                        textAlign: align
                    }}
                    key={key}
                    className={classNames(
                        Bem.cls('table-data', 'body-cell', { overflow }),
                        cls
                    )}
                >
                    {cells[index]}
                </div>
            )
        );
    };
}
